import GeoType from '@/Enums/Geography/GeographyType.enum';
import MapScale from '@/Enums/Map/MapScale.enum';

// Quintile breakpoints across positive impression values. Using max/5 linear
// stops collapses skewed distributions (one big DMA vs many small ones) into
// a single visible color band — 98% of features clamp to the first stop and
// render invisible against the basemap. Percentile stops give each colour
// band ≈20% of features regardless of skew.
const getImpressionsStops = data => {
  const positives = (data?.features ?? [])
    .map(f => Number(f?.properties?.Impressions))
    .filter(n => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  if (!positives.length) return null;
  const at = p => positives[Math.min(positives.length - 1, Math.floor(positives.length * p))];
  // Strictly ascending stops required by mapbox; nudge ties up by 1 so a
  // dataset with mostly-identical values still produces a valid expression.
  let prev = -Infinity;
  return [0.2, 0.4, 0.6, 0.8, 1.0].map(p => {
    const v = at(p);
    const next = v > prev ? v : prev + 1;
    prev = next;
    return next;
  });
};

export const dataLayerStyle = data => {
  // No positive-impression features at all (boundaries loaded before rows,
  // empty scale, sparse range): paint a flat neutral fill so the boundaries
  // are at least visible and the absence of heat is obvious.
  const stops = getImpressionsStops(data);
  if (!stops) {
    return {
      id: 'data',
      type: 'fill',
      paint: {
        'fill-color': ['case', ['has', 'Impressions'], '#D9D9D9', 'transparent'],
        'fill-opacity': 0.6,
      },
    };
  }

  return {
    id: 'data',
    type: 'fill',
    paint: {
      'fill-color': [
        'case',
        ['has', 'Impressions'],
        [
          'interpolate',
          ['linear'],
          ['get', 'Impressions'],
          stops[0],
          '#D9D9D9',
          stops[1],
          '#BCC7C1',
          stops[2],
          '#8DA199',
          stops[3],
          '#49695B',
          stops[4],
          '#1B4332',
        ],
        'transparent',
      ],
      'fill-opacity': 0.6,
    },
  };
};

export const locationLayerConfigs = [
  {
    id: 'country',
    filter: ['==', ['get', 'locationType'], GeoType.Country],
  },
  {
    id: 'state',
    filter: ['==', ['get', 'locationType'], MapScale.State],
  },
  {
    id: 'dma',
    filter: ['==', ['get', 'locationType'], MapScale.Dma],
  },
  {
    id: 'zipCode',
    filter: ['has', 'zipCode'],
  },
  {
    id: 'county',
    filter: ['==', ['get', 'locationType'], MapScale.County],
  },
  {
    id: 'congressional-district',
    filter: ['==', ['get', 'locationType'], MapScale.CongressionalDistricts],
  },
  {
    id: 'city',
    filter: ['==', ['get', 'locationType'], MapScale.City],
  },
];

export const circleLayerStyle = {
  id: 'circle',
  type: 'fill',
  source: 'circle',
  paint: {
    'fill-color': '#49695B',
    'fill-opacity': 0.6,
  },
  filter: ['==', ['get', 'type'], 'Circle'],
};

export const exclusionsLayerStyle = {
  id: 'exclusions',
  type: 'fill',
  source: 'exclusions',
  paint: {
    'fill-color': 'red',
    'fill-opacity': 0.2,
  },
};
